import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  useAccordionItemContext,
  useAccordionContext,
} from '@reach/accordion';
import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import type { IconName } from '@hotjar/hotsauce';
import { BaseIcon, Text, tokens } from '@hotjar/hotsauce';

import styled from '@emotion/styled';
import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { AccordionContext } from './AccordionContext';

export enum AccordionSectionStatus {
  ERROR,
  SUCCESS,
}

export type AccordionSize = 's' | 'm';

interface AccordionSectionSpacing {
  horizontal: string;
  vertical: string;
}

const SpacingBySize: Record<AccordionSize, AccordionSectionSpacing> = {
  s: { horizontal: tokens.spacer.x3, vertical: tokens.spacer.x2 },
  m: { horizontal: tokens.spacer.x4, vertical: tokens.spacer.x3 },
};

export interface AccordionSectionProps {
  /**
   * The title of the section.
   */
  title: ReactNode | string;
  /**
   * The description of the section.
   * Only displayed in the larger version.
   */
  description?: string;
  /**
   * The icon to be shown in the Section Header.
   * Use an icon if it compliments the title or adds additional content.
   */
  icon?: IconName;
  /**
   * The size of the icon to be shown in the Section Header.
   */
  iconSize?: number;
  /**
   * A string to be used for tests.
   */
  dataTestId?: string;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * If set, it adds custom styles to the accordion panel.
   */
  headerCustomCss?: SerializedStyles;
  /**
   * Status to be shown in the header of the section, which translates into colors.
   * Currently, 2 status are supported: error (red) and success (green).
   */
  status?: AccordionSectionStatus;
  /**
   * Sets the size of the Accordion. When size is `s` the descriptions are not rendered.
   */
  // FIXME: This appears to be unused.
  // eslint-disable-next-line react/no-unused-prop-types
  size?: AccordionSize;
  /**
   * This callback is fired everytime the section is toggled.
   * The argument contains the open status (true: open, false: closed).
   */
  onToggle?: (open: boolean) => void;
  /**
   * The content of the section.
   */
  children: ReactNode;
}

const StyledAccordionItem = styled(AccordionItem)`
  position: relative;
  border-bottom: none;
  z-index: 1;

  &:focus-within,
  &:hover {
    z-index: 2;
  }
`;

const StyledAccordionButton = styled(AccordionButton)`
  position: relative;
  z-index: 3;
  border: none;
  background: none;
  padding: ${tokens.spacer.x3} ${tokens.spacer.x3} ${tokens.spacer.x1} ${tokens.spacer.x3};
  display: flex;
  align-items: center;
  text-align: left;
  width: 100%;
  cursor: pointer;
  &:hover {
    background-color: ${tokens.color.neutral_100};
    box-shadow: 0 0 0 1px ${tokens.color.ui_border_alt};
  }
  &:focus {
    outline: none;
    // since the AccordionButton is inside the AccordionItem,
    // we need to recreate the border of AccordionItem
    box-shadow: 0 0 0 1px ${tokens.color.ui_border_alt}, 0 0 0 2px ${tokens.color.ui_background},
      0 0 0 4px ${tokens.color.focus};
  }
`;

const StyledIcon = styled(BaseIcon)`
  flex-shrink: 0;
`;

const ChevronIcon = styled(StyledIcon)`
  transform: rotate(0deg);
  ${tokens.transition('transform')};
`;

const Title = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const Header = styled.div<{ size: AccordionSize }>`
  ${({ size }) => `
    padding-right: ${SpacingBySize[size].horizontal};
    flex-grow: 1;
  `}
`;

const StyledAccordionPanel = styled(AccordionPanel)<{ size: AccordionSize }>`
  ${({ size }) => `
    position: relative;
    z-index: 2;
    padding: ${tokens.spacer.x1} ${SpacingBySize[size].horizontal} ${tokens.spacer.x2}
      ${SpacingBySize[size].horizontal};
    &:focus {
      outline: none;
    }
  `}
`;

type AccordionHeaderProps = Pick<
  AccordionSectionProps,
  'title' | 'description' | 'icon' | 'iconSize' | 'className' | 'status' | 'size' | 'onToggle'
>;

const AccordionHeader = ({
  title,
  icon,
  iconSize = 24,
  className,
  size = 's',
  onToggle,
}: AccordionHeaderProps) => {
  const { isExpanded, index } = useAccordionItemContext();
  const { onToggleSection } = useContext(AccordionContext);
  const { openPanels } = useAccordionContext();

  const openPanelsRef = useRef(openPanels);

  const triggerToggle = useCallback(
    (sectionIndex: number, open: boolean) => {
      if (onToggle) {
        onToggle(open);
      }
      if (onToggleSection) {
        onToggleSection({ index: sectionIndex, open });
      }
    },
    [onToggle, onToggleSection],
  );

  useEffect(() => {
    openPanelsRef.current = openPanels;
    const isOpen = openPanels.includes(index);
    if (isOpen) {
      triggerToggle(index, true);
    }
    return () => {
      setTimeout(() => {
        if (isOpen && !openPanelsRef.current.includes(index)) {
          triggerToggle(index, false);
        }
      }, 0);
    };
  }, [openPanels, index, triggerToggle]);

  return (
    <StyledAccordionButton className={className}>
      {icon && (
        <StyledIcon
          color="text_default"
          icon={icon}
          pixelSize={iconSize}
          css={css`
            margin-right: ${SpacingBySize[size].horizontal};
          `}
        />
      )}
      <Header size={size}>
        <Title textType="subtitle1">{title}</Title>
      </Header>
      <ChevronIcon
        color="text_default"
        icon="chevron_bottom"
        pixelSize={16}
        css={isExpanded ? { transform: 'rotate(180deg)' } : {}}
      />
    </StyledAccordionButton>
  );
};

export const AccordionSection = ({
  title,
  description,
  icon,
  iconSize,
  dataTestId,
  status,
  children,
  className,
  headerCustomCss,
  onToggle,
}: AccordionSectionProps): JSX.Element => {
  const { size } = useContext(AccordionContext);
  return (
    <StyledAccordionItem
      data-testid={dataTestId != null ? `accordion-item-${dataTestId}` : undefined}
    >
      <AccordionHeader
        title={title}
        description={description}
        icon={icon}
        iconSize={iconSize}
        className={className}
        status={status}
        onToggle={onToggle}
      />
      <StyledAccordionPanel size={size} css={headerCustomCss}>
        {children}
      </StyledAccordionPanel>
    </StyledAccordionItem>
  );
};
